import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { PeopleTwoIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getInformationRequest } from '../../api/information-requests/get-information-request'
import { updateInformationRequest } from '../../api/information-requests/update-information-request'
import { ItemNotFound } from '../../components/elements/item-not-found'
import UserNavBar from '../../components/elements/nav-bar'
import InformationRequestForm from '../../components/pages/information-request/information-request-form'
import { Paths } from '../../lib/constants/path'
import useLoading from '../../lib/hooks/use-loading'
import { InformationRequestInput } from '../../lib/types/models/information-request/information-request-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { checkForFrance, showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const InformationRequestUpdate = () => {
  const { id: informationRequestId } = useParams()
  const queryClient = useQueryClient()
  const { startLoading, stopLoading } = useLoading()
  const { navigate, back } = useNavigator()

  if (!informationRequestId || isNaN(parseInt(informationRequestId)))
    return <ItemNotFound title={'Identifiant de demande invalide'} />

  const { data, isLoading } = useQuery({
    queryKey: ['information-requests', informationRequestId],
    queryFn: () => getInformationRequest(parseInt(informationRequestId))
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateInformationRequest,
    onError: showAxiosError,
    onSuccess: (informationRequest) => {
      queryClient.invalidateQueries({ queryKey: ['information-requests'] })
      if (informationRequest.email !== data?.email) {
        startLoading("Vérifier la validation de l'e-mail")
        // Invalidate the pcr query to refetch the updated data
        setTimeout(() => {
          stopLoading()
          // I am adding the display toast and navigate to the if and else confdition since outside they don't run in order
          // example I would navigate to the page before the end of the validation of the email is done
          toast.success('Demande de brochure modifiée avec succès')
        }, 8000)
      } else {
        toast.success('Demande de brochure modifiée avec succès')
      }
    }
  })

  const onSubmit = async (values: InformationRequestInput) => {
    await mutateAsync({
      id: parseInt(informationRequestId),
      ...values,
      ...(values.country && { inFrance: checkForFrance(values.country) })
    })
  }

  if (isLoading) return <Loader />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title={`${data?.childFirstName ? StringFormatter.capitalize(data.childFirstName) : ''} ${data?.childLastName ? data.childLastName.toUpperCase() : ''}`}
        icon={PeopleTwoIcon}
      />
      <div className="h-4" />
      <Box>
        <div className="flex justify-between items-center py-2">
          <div className="flex items-center gap-4">
            <Typography variant="sub-heading">
              Veuillez modifier le formulaire ci-dessous
            </Typography>
          </div>
        </div>
        <div className="h-5" />

        <InformationRequestForm
          defaultValues={data}
          emailValidityStatus={data.emailValidityStatus}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.INFORMATION_REQUESTS_PAGE),
            onUpdate: onSubmit,
            onUpdateThenCreate: async (values) => {
              await onSubmit(values)
              navigate(Paths.INFORMATION_REQUEST_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await onSubmit(values)
              back(Paths.INFORMATION_REQUESTS_PAGE)
            }
          })}
        />
      </Box>
    </div>
  )
}

export default InformationRequestUpdate
